import {http, HttpResponse} from 'msw';
import {SetupServerApi} from 'msw/node';

import {mergeDeepRight} from 'ramda';

import {
  GetDirectSaleLabourItemResponse,
  ItemsDiscounts,
  PriceType,
} from '../../metadaWarehouseDirectSaleItemLabour/types';
import {
  ArticlePriceIndicator,
  DiscountSourceType,
  GetDirectSaleMaterialItemResponse,
} from '../../metadaWarehouseDirectSaleItemSparePart/types';

const getDirectSaleMaterialItemMock = (
  configuration: Partial<GetDirectSaleMaterialItemResponse> = {}
): GetDirectSaleMaterialItemResponse =>
  mergeDeepRight(
    {
      id: '06dcd2ab-5ce6-46b0-bf5b-0a57b82ec423',
      warehouse: {
        warehouseId: '1d015a9f-e025-4d36-aabb-706b0e96f57c',
        warehouseArticleId: '272d3033-399b-478c-b877-369edc3dbc9a',
        warehouseAction: 'NONBINDING',
      },
      name: 'mpt_001',
      number: 'MPT 001',
      neededAt: '2024-08-21',
      quantity: {
        amount: 1,
        unit: '9869f85f-99fa-4e05-8cf6-7743f535cdca',
      },
      dispensingUnit: 1,
      manufacturerId: '2f66ff8a-9825-4ddd-b69b-119c262e285c',
      manufacturerNumber: 'MPT 001',
      isUnitPriceWithVat: false,
      purchasePricePerUnit: {
        amount: 10,
        currency: 'EUR',
      },
      sellingPricePerUnit: {
        amount: 15,
        currency: 'EUR',
      },
      sellingPriceVat: {
        type: 'S',
        freeCode: '',
      },
      articlePrice: {
        withVat: {
          amount: 0,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 0,
          currency: 'EUR',
        },
      },
      articlePriceIndicator: 'NO_PRICE' as ArticlePriceIndicator,
      isCustomPrice: true,
      baseSellingPricePerUnit: {
        amount: 0,
        currency: 'EUR',
      },
      baseSellingPriceVat: {type: 'S'},
      isAllowedItemEditing: true,
      isAllowedQuantityEditing: true,
      itemsDiscounts: {
        isDoNotApplyDiscount: false,
        isDiscountEnable: false,
        availableDiscounts: [
          {
            discountSourceType: 'manual-discount' as DiscountSourceType,
            discountSourceTypeName: 'Manual discount',
            discountRate: 0,
            isSet: false,
            isChangeable: true,
          },
        ],
      },
      sellingTotalPrice: {
        withVat: {
          amount: 18.15,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 15,
          currency: 'EUR',
        },
      },
      purchaseTotalPrice: {
        withVat: {
          amount: 12.1,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 10,
          currency: 'EUR',
        },
      },
      marginTotal: {
        marginPercentage: 0.3333,
        withVat: {
          amount: 6.05,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 5,
          currency: 'EUR',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      marginPerUnit: {
        marginPercentage: 0.3333,
        withVat: {
          amount: 6.05,
          currency: 'EUR',
        },
        withoutVat: {
          amount: 5,
          currency: 'EUR',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      discount: {
        amount: 0,
        currency: 'EUR',
      },
      isOneTimeSale: false,
    },
    configuration
  );

const getDirectSaleMaterialItemInterceptor = (server: SetupServerApi) =>
  server.use(http.get(`/warehouse/v2/direct-sale/*/item-spare-part/*`, () => HttpResponse.json()));

const patchDirectSaleMaterialItemInterceptor = (server: SetupServerApi) =>
  server.use(
    http.patch('*/warehouse/v2/direct-sale/*/item-spare-part/*', () => HttpResponse.json())
  );

const getDirectSaleLabourItemMock = (
  configuration: Partial<GetDirectSaleLabourItemResponse> = {}
): GetDirectSaleLabourItemResponse =>
  mergeDeepRight(
    {
      id: '9cbf7a58-fd24-4d85-98f1-a8b39dd46e8b',
      labourCatalogId: 'db3bf62b-e3e6-45a9-88bf-558ab65bbe7f',
      labourRateId: '8878ff6f-9130-4bd5-980c-717150f42244',
      priceType: 'SVCPRICETYPE_TIME_NORM' as PriceType,
      name: '001TN',
      number: '001',
      workType: '413f4030-19f1-4fd9-995e-0416373a2e83',
      quantity: {
        amount: 1,
        unit: 'hod.',
      },
      isUnitPriceWithVat: false,
      purchasePricePerUnit: {amount: 100, currency: 'CZK'},
      sellingPricePerUnit: {
        amount: 150,
        currency: 'CZK',
      },
      sellingPriceVat: {type: 'S'},
      isCustomPrice: false,
      basePurchasePricePerUnit: {amount: 100, currency: 'CZK'},
      baseSellingPricePerUnit: {
        amount: 150,
        currency: 'CZK',
      },
      baseSellingPriceVat: {type: 'S'},
      itemEditingAllowed: true,
      quantityEditingAllowed: true,
      itemsDiscounts: {
        isDoNotApplyDiscount: false,
        isDiscountEnable: false,
        availableDiscounts: [
          {
            discountSourceType: 'manual-discount',
            discountSourceTypeName: 'Manual discount',
            discountRate: 0,
            isSet: false,
            isChangeable: true,
          },
        ],
      } as ItemsDiscounts,
      sellingTotalPrice: {
        withVat: {
          amount: 181.5,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 150,
          currency: 'CZK',
        },
      },
      purchaseTotalPrice: {
        withVat: {
          amount: 0,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 0,
          currency: 'CZK',
        },
      },
      marginTotal: {
        marginPercentage: 1,
        withVat: {
          amount: 181.5,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 150,
          currency: 'CZK',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      marginPerUnit: {
        marginPercentage: 1,
        withVat: {
          amount: 181.5,
          currency: 'CZK',
        },
        withoutVat: {
          amount: 150,
          currency: 'CZK',
        },
        isMarginUnderMinimum: false,
        isMarginExisting: true,
      },
      discount: {
        amount: 0,
        currency: 'CZK',
      },
    },
    configuration
  );

const getDirectSaleLabourItemInterceptor = (server: SetupServerApi) =>
  server.use(http.get(`/warehouse/v1/direct-sale/*/item-labour/*`, () => HttpResponse.json()));

const patchDirectSaleLabourItemInterceptor = (server: SetupServerApi) =>
  server.use(http.patch('*/warehouse/v1/direct-sale/*/item-labour/*', () => HttpResponse.json()));

export const metadaWarehouseDirectSaleItemApiMock = {
  getDirectSaleMaterialItemMock,
  getDirectSaleMaterialItemInterceptor,
  patchDirectSaleMaterialItemInterceptor,
  getDirectSaleLabourItemMock,
  getDirectSaleLabourItemInterceptor,
  patchDirectSaleLabourItemInterceptor,
};
